import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Layout, SupportIcon, Title } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FloatingField } from "../components/FloatingField";
import { Popup } from "../components/Popup";
import { CvvPrepareGate } from "../components/CvvPrepareGate";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRecentRecipients } from "../hooks/useRecentRecipients";
import {
  checkCipReadiness,
  getWidgetApiError,
  isDeadLinkError,
} from "../services/api";
import { toTitleCase } from "../utils/titleCase";
import { prepareFundingRail } from "../utils/prepare-funding-rail";
import { reasonFromCardPayCode } from "../utils/card-funding-availability";
import { blockRailCard } from "../utils/blocked-rail-cards";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import { categoryFromFundingMethod } from "../utils/catalog";
import {
  isFundingInstrumentReady,
  type FundingInstrument,
  type FundingInstrumentSaved,
} from "../interfaces/funding";
import { paymentType } from "../interfaces/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { getEvents, safeCapture, trackScreenView } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { Navbar } from "../components/Navbar";


/**
 * Recipient identity step: recent picks + free-text legal name (Title Case),
 * then funding prepare (CARD) → CIP gate → minting.
 */
export const RecipientPage = memo(() => {
  useClaimLink();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    theme,
    paymentState,
    setPaymentState,
    fundingInstrument,
    setFundingInstrument,
    setPreparedFundingPaymentMethodId,
  } = useStore();
  const { session } = useSessionStore();
  const {
    composeDraft,
    draft,
    setDraft,
    pendingRecipientName,
    setPendingRecipientName,
    setPendingFundingMethod,
  } = useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();

  const linkId = linkIdFromRoute || session.data?.linkId;
  const userStatus = session.data?.userStatus;
  const canOriginate = !userStatus?.blocked && !userStatus?.suspended;
  const { cards, refresh: refreshPaymentMethods } = usePaymentMethods(linkId);
  const { recipients: recentRecipients, loading: loadingRecipients } =
    useRecentRecipients(linkId);
  // Shimmer only on the first load: a refresh keeps the current list visible
  // instead of collapsing it.
  const recentBusy = loadingRecipients && recentRecipients.length === 0;

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } = colorConfig;
  const { openSupport, supportSheet } = useSupportSheet();
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };

  useEffect(() => {
    trackScreenView("recipient");
  }, []);

  const seededName = pendingRecipientName || draft?.recipientName || "";
  const [recipientName, setRecipientNameState] = useState(() =>
    seededName ? toTitleCase(seededName) : ""
  );
  const [shakeRecipient, setShakeRecipient] = useState(false);
  const [cipLoading, setCipLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [cvvGate, setCvvGate] = useState<{
    last4: string;
    brand?: string;
    funding: FundingInstrumentSaved;
    error: string | null;
  } | null>(null);

  const setRecipientName = useCallback(
    (name: string) => {
      const formatted = toTitleCase(name);
      setRecipientNameState(formatted);
      setPendingRecipientName(formatted);
    },
    [setPendingRecipientName]
  );

  useEffect(() => {
    if (!composeDraft) {
      redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
    }
  }, [composeDraft, redirectTo]);

  const trimmedRecipient = recipientName.trim();
  const hasRecipient =
    trimmedRecipient.split(/\s+/).filter(Boolean).length >= 2 &&
    trimmedRecipient.length >= 3;

  const resolveFundingInstrument = useCallback((): FundingInstrument | null => {
    if (fundingInstrument && isFundingInstrumentReady(fundingInstrument)) {
      return fundingInstrument;
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      paymentState.payment.cardId
    ) {
      const card = cards.find(
        (c) => c.paymentMethodId === paymentState.payment!.cardId
      );
      if (card) {
        return {
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        };
      }
    }
    return null;
  }, [fundingInstrument, paymentState.payment, cards]);

  const prepareErrorMessage = useCallback(
    (code?: string) => {
      switch (code) {
        case "CVV_REQUIRED":
          return t("transfer.prepare.errorCvv");
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        case "VGS_CONFIG_UNAVAILABLE":
          return t("vgs.configUnavailable");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t]
  );

  const proceedToCipOrMint = useCallback(
    async (fundingPaymentMethodId?: string) => {
      if (!canOriginate) {
        redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
        return;
      }
      if (!composeDraft) return;

      setDraft({
        ...composeDraft,
        recipientName: trimmedRecipient,
        ...(fundingPaymentMethodId
          ? { fundingPaymentMethodId }
          : {}),
      });
      setPendingRecipientName(trimmedRecipient);

      if (!linkId) {
        redirectTo(APP_ROUTES.MINTING_PAGE);
        return;
      }

      try {
        setCipLoading(true);
        const cip = await checkCipReadiness(linkId);
        if (cip.shouldRunCip && cip.cipTicketId) {
          redirectTo(APP_ROUTES.CIP_PAGE, {
            replace: true,
            state: { cipTicketId: cip.cipTicketId },
          });
          return;
        }
        const prompt = shouldPromptPhone(session.data, linkId);
        if (prompt) {
          redirectTo(APP_ROUTES.PHONE_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.MINTING_PAGE },
          });
          return;
        }
        await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
        redirectTo(APP_ROUTES.MINTING_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.MINTING_PAGE },
        });
      } catch (err) {
        // Link expired / revoked: the dead-link handler already routed to the
        // screen that explains why the link stopped working.
        if (isDeadLinkError(err)) {
          return;
        }

        const apiCode = getWidgetApiError(err)?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.transfersDisabledTitle"),
              subTitle: null,
              message: t("error.transfersDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }

        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.title"),
            subTitle: null,
            message: t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.RECIPIENT_PAGE,
          },
        });
      } finally {
        setCipLoading(false);
      }
    },
    [
      canOriginate,
      composeDraft,
      trimmedRecipient,
      setDraft,
      setPendingRecipientName,
      linkId,
      redirectTo,
      t,
      i18n,
    ]
  );

  const handleConfirm = useCallback(
    async (cvv?: string) => {
      if (!composeDraft) {
        redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
        return;
      }
      if (!hasRecipient) {
        setShakeRecipient(true);
        setTimeout(() => setShakeRecipient(false), 500);
        return;
      }
      if (cipLoading) return;

      safeCapture(getEvents().RECIPIENT_NAME_SUBMIT, { success: true });

      const fundingCategory = categoryFromFundingMethod(
        composeDraft.fundingMethod
      );
      const needsCardPrepare =
        fundingCategory === "CARD" && Boolean(composeDraft.capabilityKey);

      if (!needsCardPrepare) {
        await proceedToCipOrMint(composeDraft.fundingPaymentMethodId);
        return;
      }

      if (!linkId) {
        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.title"),
            subTitle: null,
            message: t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.RECIPIENT_PAGE,
          },
        });
        return;
      }

      const instrument = resolveFundingInstrument();
      if (!instrument) {
        setPrepareError(t("transfer.prepare.errorGeneric"));
        return;
      }

      setCipLoading(true);
      setPrepareError(null);

      try {
        const fundingResult = await prepareFundingRail({
          linkId,
          funding: instrument,
          capabilityKey: composeDraft.capabilityKey!,
          cvv,
          ...(composeDraft.fundingMethod === "DEBIT_CARD" ||
          composeDraft.fundingMethod === "CREDIT_CARD"
            ? { fundingMethod: composeDraft.fundingMethod }
            : {}),
        });

        setFundingInstrument(fundingResult.funding);
        const preparedId = fundingResult.funding.paymentMethodId;
        setPreparedFundingPaymentMethodId(preparedId);
        // New register or provision — keep the saved-card list in sync.
        void refreshPaymentMethods();

        setPaymentState({
          payment: {
            cardId: preparedId,
            type: paymentType.CARD,
            brand: `${(fundingResult.funding.brand || "").toUpperCase()}-${fundingResult.funding.last4}`,
          },
        });

        if (fundingResult.status === "needs_cvv") {
          setCvvGate({
            last4: fundingResult.funding.last4,
            brand: fundingResult.funding.brand,
            funding: fundingResult.funding,
            error:
              fundingResult.errorCode &&
              fundingResult.errorCode !== "CVV_REQUIRED"
                ? prepareErrorMessage(fundingResult.errorCode)
                : null,
          });
          return;
        }

        if (fundingResult.status === "failed") {
          const reason = reasonFromCardPayCode(
            fundingResult.errorCode,
            fundingResult.retryLater,
          );
          if (
            reason === "railRejected" ||
            reason === "railTemporarilyUnavailable"
          ) {
            const paymentMethodId = fundingResult.funding.paymentMethodId;
            blockRailCard(paymentMethodId, reason);
            setFundingInstrument(null);
            setPaymentState({ payment: null });
            setPendingFundingMethod(null);
            setPreparedFundingPaymentMethodId(null);
            const listed = cards.find(
              (card) => card.paymentMethodId === paymentMethodId,
            );
            const sheetCard: ICardPaymentMethod = listed ?? {
              paymentMethodId,
              last4: fundingResult.funding.last4,
              brand: fundingResult.funding.brand,
              expiryMonth: "",
              expiryYear: "",
              enabled: true,
              isDefault: false,
              fundingMethod: fundingResult.funding.fundingMethod,
            };
            redirectTo(APP_ROUTES.TRANSFER_PAGE, {
              state: { railUnavailable: { card: sheetCard, reason } },
            });
            return;
          }
          setPrepareError(prepareErrorMessage(fundingResult.errorCode));
          setCvvGate(null);
          return;
        }

        setCvvGate(null);
        await proceedToCipOrMint(preparedId);
      } catch (err) {
        const code =
          err && typeof err === "object" && "code" in err
            ? String((err as { code?: string }).code)
            : undefined;
        if (code === "REGISTER_FAILED" || code === "VGS_CONFIG_UNAVAILABLE") {
          setPrepareError(prepareErrorMessage(code));
          return;
        }
        setPrepareError(t("transfer.prepare.errorGeneric"));
      } finally {
        setCipLoading(false);
      }
    },
    [
      composeDraft,
      hasRecipient,
      cipLoading,
      linkId,
      resolveFundingInstrument,
      prepareErrorMessage,
      setFundingInstrument,
      setPreparedFundingPaymentMethodId,
      setPaymentState,
      refreshPaymentMethods,
      proceedToCipOrMint,
      redirectTo,
      cards,
      setPendingFundingMethod,
      t,
    ]
  );

  if (!composeDraft) return null;

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(-1)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="recipient-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <Title title={t("recipient.title")} align="left" color={TITLE_COLOR} />
          <p className="recipient-name-hint">{t("recipient.description")}</p>

          <section
            className={`recipient-name-field ${shakeRecipient ? "shake" : ""}`}
          >
            {recentBusy && (
              <section
                className="recipient-recent"
                aria-busy="true"
                aria-label={t("recipient.loadingRecent")}
              >
                <span className="recipient-recent-label">
                  {t("recipient.recentLabel")}
                </span>
                <div className="recipient-recent-list" aria-hidden="true">
                  {Array.from({ length: 2 }, (_, i) => (
                    <div key={i} className="recipient-recent-skeleton">
                      <span className="recipient-recent-skeleton-line" />
                      <span className="recipient-recent-skeleton-line short" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!recentBusy && recentRecipients.length > 0 && (
              <section
                className="recipient-recent"
                aria-label={t("recipient.recentAria")}
              >
                <span className="recipient-recent-label">
                  {t("recipient.recentLabel")}
                </span>
                <div role="list" className="recipient-recent-list">
                  {recentRecipients.map((entry) => {
                    const selected =
                      trimmedRecipient.toLowerCase() ===
                      entry.fullName.toLowerCase();
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        role="listitem"
                        className={`recipient-recent-item${selected ? " selected" : ""}`}
                        onClick={() => setRecipientName(entry.fullName)}
                      >
                        <span className="recipient-recent-name">
                          {entry.fullName}
                        </span>
                        <span
                          className="recipient-recent-meta"
                          style={{ color: bodyCopy }}
                        >
                          {t("recipient.timesUsed", {
                            count: entry.timesUsed,
                          })}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <span className="recipient-or-new">
                  {t("recipient.orEnterNew")}
                </span>
              </section>
            )}

            <div className="recipient-input-label">
              <FloatingField
                label={t("recipient.fullNameLabel")}
                value={recipientName}
                colors={fieldColors}
              >
                <input
                  type="text"
                  style={{ color: bodyCopy }}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  autoComplete="name"
                  autoCapitalize="words"
                />
              </FloatingField>
            </div>
            <span className="recipient-name-field-hint">
              {t("recipient.fullNameHint")}
            </span>
          </section>

          {prepareError ? (
            <p className="cvv-prepare-gate__error" role="alert">
              {prepareError}
            </p>
          ) : null}
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <div
              className={`button-wrapper ${
                hasRecipient || cipLoading ? "rainbow-ready" : ""
              }`}
            >
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!hasRecipient || cipLoading}
                onClick={() => void handleConfirm()}
              >
                {cipLoading
                  ? t("transfer.prepare.loading")
                  : t("common.continue")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      {cvvGate ? (
        <Popup
          visible
          onClose={() => {
            setCvvGate(null);
            setPrepareError(null);
          }}
        >
          <CvvPrepareGate
            last4={cvvGate.last4}
            brand={cvvGate.brand}
            loading={cipLoading}
            error={cvvGate.error}
            onClose={() => {
              setCvvGate(null);
              setPrepareError(null);
            }}
            onSubmit={(enteredCvv) => {
              void handleConfirm(enteredCvv);
            }}
          />
        </Popup>
      ) : null}
      {supportSheet}
    </>
  );
});
